import { z } from 'zod/v3';

export const NotificationChannelSchema = z.enum(['inbox', 'teams', 'email', 'slack']);
export type NotificationChannel = z.infer<typeof NotificationChannelSchema>;

export const NotificationDeliverySchema = z.enum(['instant', 'digest', 'off']);
export type NotificationDelivery = z.infer<typeof NotificationDeliverySchema>;

export const NotificationClassSchema = z.enum([
  // Existing inbox item types
  'agent_escalated',
  // Vera hands back an interactive decision (agent choice, runbook human step)
  // rather than a batched "here are N tests" escalation. Split from
  // agent_escalated so the batch can be digested daily while an approval that
  // carries its own approve/reject payload still delivers instantly.
  'agent_approval_requested',
  'agent_resolved',
  'agent_batch_complete',
  'auditor_comment',
  // RFC-233 r1.1: an auditor minted a pull-API token; admins learn at mint time.
  'auditor_api_token_minted',
  'ces_assigned',
  'ces_unassigned',
  'ces_assignee_changed',
  'ces_mentioned',
  'ces_commented',
  'ces_status_changed',
  'ces_due_date_changed',
  'policy_approval_requested',
  'policy_approved',
  'policy_returned_for_changes',
  'policy_decided',
  'test_approval_requested',
  'test_approved',
  'test_returned_for_changes',
  'test_decided',
  // Vendor assessment review workflow (CC9.2): initiator submits → reviewer is
  // pinged; reviewer decides → initiator is pinged.
  'vendor_assessment_review_requested',
  'vendor_assessment_decided',
  // Risk assessment review workflow (CC3.x): initiator submits → reviewer is
  // pinged; reviewer decides → initiator is pinged.
  'risk_assessment_review_requested',
  'risk_assessment_decided',
  'integration_disconnected',
  'integration_evidence_synced',
  'evidence_suggested',
  // Existing queued notification categories
  'task_assigned',
  'access_review_item',
  'task_reminder',
  'task_weekly_digest',
  'task_comment',
  'comment_mention',
  'task_description_mention',
  'approval_request',
  'security_alert',
  'billing_failure',
  // RFC-189 / RFC-190 loop events
  'test_regressed',
  'retest_due',
  'document_review_due',
  'evidence_expired',
  'compliance_exception_expiry',
  'finding_escalated',
  'vera_daily_plan',
  'daily_briefing',
  'check_quarantined',
  'sla_violation',
]);
export type NotificationClass = z.infer<typeof NotificationClassSchema>;

export const NotificationRoutingSchema = z.object({
  channels: z.array(NotificationChannelSchema),
  delivery: NotificationDeliverySchema,
});
export type NotificationRouting = z.infer<typeof NotificationRoutingSchema>;

export const OrgNotificationPolicyDTOSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  class: NotificationClassSchema,
  channels: z.array(NotificationChannelSchema),
  delivery: NotificationDeliverySchema,
});
export type OrgNotificationPolicyDTO = z.infer<typeof OrgNotificationPolicyDTOSchema>;
